import { useState, useEffect, useRef, useCallback } from 'react'

// PDF.js loaded from CDN on demand
let pdfjs = null
async function getPdfJs() {
  if (pdfjs) return pdfjs
  const mod = await import('https://cdn.jsdelivr.net/npm/pdfjs-dist@4.2.67/build/pdf.min.mjs')
  mod.GlobalWorkerOptions.workerSrc =
    'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.2.67/build/pdf.worker.min.mjs'
  pdfjs = mod
  return pdfjs
}

export const PdfViewer = ({ url, fileName, onClose, onDownload }) => {
  const canvasRef  = useRef(null)
  const [doc,      setDoc]      = useState(null)
  const [page,     setPage]     = useState(1)
  const [total,    setTotal]    = useState(0)
  const [scale,    setScale]    = useState(1.2)
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState(null)
  const renderRef  = useRef(null)

  // Load PDF document
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    ;(async () => {
      try {
        const lib    = await getPdfJs()
        const loaded = await lib.getDocument({ url }).promise
        if (!cancelled) {
          setDoc(loaded)
          setTotal(loaded.numPages)
          setPage(1)
          setLoading(false)
        }
      } catch (e) {
        if (!cancelled) {
          setError('Failed to load PDF.')
          setLoading(false)
        }
      }
    })()
    return () => { cancelled = true }
  }, [url])

  // Render current page to canvas
  const renderPage = useCallback(async () => {
    if (!doc || !canvasRef.current) return
    if (renderRef.current) renderRef.current.cancel()

    const pdfPage = await doc.getPage(page)
    const viewport = pdfPage.getViewport({ scale })
    const canvas   = canvasRef.current
    const ctx      = canvas.getContext('2d')

    canvas.width  = viewport.width
    canvas.height = viewport.height

    renderRef.current = pdfPage.render({ canvasContext: ctx, viewport })
    try {
      await renderRef.current.promise
    } catch {
      // Cancelled render — ignore
    }
  }, [doc, page, scale])

  useEffect(() => { renderPage() }, [renderPage])

  return (
    <div className="fixed inset-0 z-[70] bg-black/90 flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 bg-black/60 text-white shrink-0">
        <span className="text-sm font-medium truncate max-w-xs">{fileName}</span>
        <div className="flex items-center gap-3">
          {/* Zoom */}
          <button onClick={() => setScale((s) => Math.max(0.5, s - 0.2))} className="text-white hover:text-gray-300 text-lg font-bold">−</button>
          <span className="text-xs w-10 text-center">{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale((s) => Math.min(3, s + 0.2))} className="text-white hover:text-gray-300 text-lg font-bold">+</button>
          {/* Page nav */}
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
            className="disabled:opacity-30 text-white hover:text-gray-300">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
          </button>
          <span className="text-xs w-16 text-center">{page} / {total}</span>
          <button onClick={() => setPage((p) => Math.min(total, p + 1))} disabled={page >= total}
            className="disabled:opacity-30 text-white hover:text-gray-300">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
          </button>
          {/* Download */}
          <button onClick={onDownload} className="text-white hover:text-gray-300">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>
          </button>
          {/* Close */}
          <button onClick={onClose} className="text-white hover:text-gray-300">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
      </div>

      {/* Canvas area */}
      <div className="flex-1 overflow-auto flex items-start justify-center p-4">
        {loading && (
          <div className="text-white mt-20">Loading PDF…</div>
        )}
        {error && (
          <div className="text-red-400 mt-20">{error}</div>
        )}
        {!loading && !error && (
          <canvas ref={canvasRef} className="shadow-2xl rounded" />
        )}
      </div>
    </div>
  )
}
